import React, { useEffect, useRef, useState } from "react";
import {
  API,
  DATASETS,
  addressFor,
  comparisonLabel,
  dateLabel,
  fetchJSON,
  money,
  numeric,
  present,
  priceDifference,
} from "./propertyData";

export default function PropertyDetails({
  feature,
  mode,
  cachedData,
  onClose,
  onLoaded,
}) {
  const [state, setState] = useState(() =>
    cachedData ? { data: cachedData } : { loading: true }
  );
  const [retry, setRetry] = useState(0);
  const closeButton = useRef(null);
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  useEffect(() => {
    if (cachedData) {
      setState({ data: cachedData });
      return;
    }
    const controller = new AbortController();
    setState({ loading: true });
    const id = feature.properties._id;
    if (!present(id)) {
      setState({ error: "Details are unavailable for this record." });
      return () => controller.abort();
    }
    fetchJSON(
      `${API}/api/${DATASETS[mode].detail}/detail?${new URLSearchParams({
        objId: id,
      })}`,
      controller.signal
    )
      .then((data) => {
        if (controller.signal.aborted) return;
        if (
          !data ||
          typeof data !== "object" ||
          Array.isArray(data) ||
          !present(data._id)
        )
          throw new Error("Invalid property response");
        setState({ data });
        onLoaded(id, data);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setState({
            error: "Could not load this property. Please try again.",
          });
      });
    return () => controller.abort();
  }, [feature, mode, retry, onLoaded, cachedData]);
  const data = state.data;
  const isSale = mode === "PPRPrice" || mode === "matchedWithPPR";
  const difference =
    data && isSale ? priceDifference(data.price, data.ppr_price) : null;
  const fields = data
    ? [
        ["Sale date", isSale ? dateLabel(data.ppr_sale_date) : null],
        ["Listed on", dateLabel(data.published_date)],
        ["County", data.county],
        ["Eircode routing key", data.eircode_routing_key],
        ["Property type", data.property_type],
        ["Bedrooms", numeric(data.beds)],
        ["Bathrooms", numeric(data.baths)],
        [
          "Floor area",
          numeric(data.m_squared) === null ? null : `${data.m_squared} m²`,
        ],
        ["BER", present(data.ber) ? String(data.ber).toUpperCase() : null],
        ["Agent", data.clean_agent],
        ["Year built", data.constructed_date],
      ].filter(([, value]) => present(value))
    : [];
  return (
    <section
      className="property-details"
      aria-labelledby="property-heading"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="section-heading">
        <span className="eyebrow">Property details</span>
        <button
          ref={closeButton}
          onClick={onClose}
          aria-label="Close property details"
        >
          ×
        </button>
      </div>
      {state.loading && <p role="status">Loading property details…</p>}
      {state.error && (
        <div role="alert">
          <p>{state.error}</p>
          <button onClick={() => setRetry((n) => n + 1)}>Try again</button>
        </div>
      )}
      {data && (
        <>
          <h2 id="property-heading" className="address">
            {addressFor(data)}
          </h2>
          <p className="eyebrow">
            {isSale ? "Sale price" : DATASETS[mode].price}
          </p>
          <p className="hero-price">
            {money(isSale ? data.ppr_price : data.price)}
          </p>
          {isSale && (
            <div className="comparison">
              <span>
                Asking price <strong>{money(data.price)}</strong>
              </span>
              <strong>{comparisonLabel(difference)}</strong>
              {difference !== null && (
                <small>
                  Difference calculated as (sale − asking) ÷ asking. A price
                  difference is not a valuation.
                </small>
              )}
              <small>
                {difference !== null
                  ? "Matched listing. Check the listing and sale dates when comparing prices."
                  : "A complete asking and sale price pair is unavailable."}
              </small>
            </div>
          )}
          <dl>
            {fields.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </dl>
          <p className="muted">
            Map locations are approximate. Missing fields are omitted.
            Historical listings may not describe the property at the time of
            sale.
          </p>
        </>
      )}
    </section>
  );
}
